import type { ContractParty, ContractReconciliationSummary } from "@closer/shared";
import {
  CONTRACT_RECONCILIATION_STATUS_NAMES,
  CONTRACT_RECONCILIATION_TASK_STATUS_NAMES,
} from "@closer/shared";
import {
  Badge,
  Button,
  Card,
  FormField,
  Input,
  Modal,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from "@closer/ui";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { ReconciliationDecisionDialog } from "../../components/ReconciliationDecisionDialog";
import { StateView } from "../../components/StateView";
import { UyeListesiDurumu } from "../../components/UyeListesiDurumu";
import { WorkflowCancelDialog } from "../../components/WorkflowCancelDialog";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { sozlesmeAdaylariSorgusu, sozlesmeSurumSorgusu } from "../../lib/sharedQueries";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * TARAFLAR ARASI MUTABAKAT (CLOS-161 `[154]` · CLOS-162 `[155]` · CLOS-163 `[156]`).
 *
 * ### ⚠ Mutabakat, İÇ ONAY DEĞİL
 *
 * Burada karar veren kişi bir TARAFI temsilen konuşuyor; iç onay organizasyonun kendi
 * zinciri. Bu yüzden her görev bir tarafa bağlı ve temsilci seçimi taraf seçimiyle
 * birlikte yapılıyor.
 *
 * ### ⚠ Bir kişi yalnız BİR tarafı temsil edebilir
 *
 * Sunucunun kuralı; ekran da aynı kişiyi ikinci tarafa eklemeyi engelliyor. İki tarafı da
 * aynı kişi temsil etseydi "mutabakat" tek kişinin kendisiyle anlaşması olurdu.
 */
export function ContractReconciliationSection({
  contractId,
  arsivde = false,
}: {
  contractId: string;
  /**
   * ⚠ Arşivdeki sözleşmede AKIŞ BAŞLATILMIYOR (kusur avı, 26 Ağustos). Sunucu dördünü de
   * reddediyor ("Arşivdeki sözleşme ... "); düğmeyi çizmek, kullanıcıyı formu doldurup
   * 409 yemeye göndermekti. Yürüyen akışın komutları KALIYOR: arşivleme onları bitirmiyor.
   */
  arsivde?: boolean;
}) {
  const { activeTenantId, can, user } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();

  const [baslatAcik, setBaslatAcik] = useState(false);
  const [kararHedef, setKararHedef] = useState<{
    reconciliationId: string;
    taskId: string;
  } | null>(null);

  const anahtar = tenantKey(tenantId, "contract", contractId, "mutabakatlar");
  const akislar = useQuery({
    queryKey: anahtar,
    queryFn: async () =>
      (
        await api<ContractReconciliationSummary[]>(`/api/contracts/${contractId}/reconciliations`, {
          tenantId,
        })
      ).data,
  });

  /**
   * ⚠ BEKLEYEN İŞLER de tazeleniyor (kusur avı, 26 Ağustos). Bu bölümdeki her karar
   * sunucuda bekleyen görev satırını kapatıyor ya da açıyor; yalnız kendi anahtarını
   * tazeleseydik üst bardaki rozet ve Ana Sayfa bölümü eski sayıda kalır, kullanıcı biraz
   * önce bitirdiği işi hâlâ "bekliyor" diye görürdü. Üç tüketici de burada: panel rozeti
   * (CLOS-156 `[149]`), Ana Sayfa bölümü ve tam liste sayfası.
   */
  const yenile = async () => {
    await queryClient.invalidateQueries({ queryKey: anahtar });
    await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "pending-tasks") });
    await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "home") });
    await queryClient.invalidateQueries({ queryKey: ["pending-tasks"] });
  };

  /** Onay bekleyen iptal hedefi; `null` iken diyalog kapalı (CLOS-968). */
  const [iptalHedefi, setIptalHedefi] = useState<string | null>(null);

  /**
   * GERİ ÇEKME (Versiyon Bazlı Workflow raporu §9.2).
   *
   * ⚠ İPTALDEN AYRI: iptal AÇIK süreci kapatır ve süreci yöneten kişinin işidir; geri çekme
   * VERİLMİŞ bir beyanı bozar ve beyanı verenin işidir. Aynı düğmeye indirseydik "kim neyi
   * geri aldı" sorusu ekranda cevapsız kalırdı.
   */
  const [geriCekHedefi, setGeriCekHedefi] = useState<{
    reconciliationId: string;
    taskId: string;
  } | null>(null);
  const [geriCekGerekce, setGeriCekGerekce] = useState("");

  const geriCek = useMutation({
    mutationFn: async (hedef: { reconciliationId: string; taskId: string; reason: string }) =>
      api(
        `/api/contracts/${contractId}/reconciliations/${hedef.reconciliationId}/tasks/${hedef.taskId}/withdraw`,
        { method: "POST", tenantId, body: JSON.stringify({ reason: hedef.reason }) },
      ),
    onSuccess: async () => {
      setGeriCekHedefi(null);
      setGeriCekGerekce("");
      await yenile();
      toast.success("Mutabakat beyanı geri çekildi");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Mutabakat geri çekilemedi"),
  });

  const iptal = useMutation({
    mutationFn: async (reconciliationId: string) =>
      api(`/api/contracts/${contractId}/reconciliations/${reconciliationId}/cancel`, {
        method: "POST",
        tenantId,
      }),
    onSuccess: async () => {
      setIptalHedefi(null);
      await yenile();
      toast.success("Mutabakat akışı iptal edildi");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Mutabakat iptal edilemedi"),
  });

  /**
   * ⚠ ÖN KOŞUL MODAL AÇILMADAN ÖLÇÜLÜYOR — canlı tur bulgusu (4 Eylül).
   *
   * `taraflar` sorgusu detay sayfasının kullandığı ANAHTARIN AYNISI, yani bu iki satır
   * pratikte önbellekten okunuyor; ek istek yalnız üye listesi için gidiyor. Bir isteğin
   * bedeli, kullanıcıyı dolduramayacağı bir formu doldurmaya göndermekten düşük.
   */
  const onKosulTaraflar = useQuery({
    /** ⚠ İzin de kapıda: yetkisiz görüntüleyicide bu iki uç HİÇ çağrılmıyor. */
    enabled: !arsivde && can("contract.reconcile"),
    queryKey: tenantKey(tenantId, "contract", contractId, "taraflar"),
    queryFn: async () =>
      (
        await api<{ parties: ContractParty[]; missing: string[] }>(
          `/api/contracts/${contractId}/parties`,
          { tenantId },
        )
      ).data,
  });
  const onKosulUyeler = useQuery({
    enabled: !arsivde && can("contract.reconcile"),
    ...sozlesmeAdaylariSorgusu(tenantId, contractId),
  });

  /**
   * ⚠ Yükleme sırasında engel GÖSTERİLMİYOR: iki sorgu dönmeden taraf sayısı 0 görünür ve
   * ekran her sözleşmede bir an "önce karşı tarafı ekleyin" diye yanlış suçlama yapardı.
   * Veri gelmemişken düğme açık kalıyor; modalın içindeki aynı kural son savunma olarak
   * duruyor (`mutabakatBaslatmaEngeli` ön koşulu ilk dalında çağırıyor).
   */
  const onKosul =
    onKosulTaraflar.isSuccess && onKosulUyeler.isSuccess
      ? mutabakatOnKosulEngeli({
          tarafSayisi: (onKosulTaraflar.data?.parties ?? []).length,
          aktifUyeSayisi: (onKosulUyeler.data?.candidates ?? []).length,
        })
      : null;

  /**
   * ⚠ Yetki kapısı BÜTÜN hook'lardan SONRA: erken `return` bir hook'un önüne geçseydi
   * React çağrı sırasını kaybederdi. Kapının kendisi değişmedi.
   */
  if (!can("contract.reconcile")) return null;

  const acik = (akislar.data ?? []).find((a) => a.status === "open");

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="mr-auto text-base font-semibold text-fg-heading">Taraf mutabakatı</h2>
        {!acik && !arsivde ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/*
              ⚠ SEBEP DÜĞMENİN YANINDA, MODALIN İÇİNDE DEĞİL — canlı tur bulgusu
              (4 Eylül). Bu cümle modalın içinde duruyordu: kullanıcı düğmeye basıyor,
              sürüm seçiyor, tarih giriyor, temsilci menülerini karıştırıyor ve ancak o
              emekten sonra "önce kullanıcı davet edin" yazısını görüyordu. Engel modalın
              içinde düzeltilemediği için (başka ekrana gitmek gerekiyor) modal artık hiç
              açılmıyor.
            */}
            {onKosul ? (
              <p className="text-xs text-warning" data-testid="mutabakat-on-kosul">
                {onKosul}
              </p>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => setBaslatAcik(true)}
              disabled={onKosul !== null}
            >
              Mutabakat başlat
            </Button>
          </div>
        ) : null}
      </div>

      {akislar.isPending ? (
        <Skeleton lines={3} />
      ) : akislar.error ? (
        <StateView
          state={resolveErrorState(akislar.error, {
            idempotent: true,
            overrides: {
              error: { title: "Mutabakat yüklenemedi", description: "Bölüm şu an getirilemedi." },
            },
          })}
          onRetry={() => void akislar.refetch()}
        />
      ) : (akislar.data ?? []).length === 0 ? (
        <p className="text-sm text-fg-muted">Bu sözleşme için henüz mutabakat başlatılmadı.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {(akislar.data ?? []).map((akis) => (
            <div
              key={akis.id}
              className="flex flex-col gap-2 rounded-md border border-border-default p-3"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={akis.status === "agreed" ? "success" : "neutral"}>
                  {CONTRACT_RECONCILIATION_STATUS_NAMES[akis.status]}
                </Badge>
                <span className="text-xs text-fg-soft">
                  Sürüm {akis.versionNumber} · {akis.startedByName}
                  {akis.dueDate
                    ? ` · Son tarih ${new Date(akis.dueDate).toLocaleDateString("tr-TR")}`
                    : ""}
                </span>
                {akis.status === "open" ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setIptalHedefi(akis.id)}
                  >
                    İptal et
                  </Button>
                ) : null}
              </div>

              <ul className="flex flex-col">
                {akis.tasks.map((gorev) => (
                  <li
                    key={gorev.id}
                    className="flex flex-wrap items-center justify-between gap-2 border-b border-border-default py-1.5 last:border-0"
                  >
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate text-sm text-fg">{gorev.partyName}</span>
                      <span className="truncate text-xs text-fg-soft">
                        {gorev.assigneeName}
                        {gorev.decisionNote ? ` · ${gorev.decisionNote}` : ""}
                      </span>
                      {/*
                        ⚠ GERİ ÇEKME GEÇMİŞİ, İLK KARARIN YERİNE DEĞİL YANINA yazılıyor.
                        Rapor §9.2 ekranda ikisini birden istiyor: "İlk mutabakat … · Geri
                        çekilme … · Gerekçe …". Üstüne yazsaydık, sunucuda özenle korunan
                        geçmiş ekranda kaybolurdu — kayıt doğru, ekran yalan söylerdi.
                      */}
                      {gorev.status === "withdrawn" && gorev.withdrawnAt ? (
                        <span className="text-xs text-warning">
                          Geri çeken: {gorev.withdrawnByName ?? "Bilinmiyor"} ·{" "}
                          {new Date(gorev.withdrawnAt).toLocaleDateString("tr-TR")}
                          {gorev.withdrawalReason ? ` · ${gorev.withdrawalReason}` : ""}
                        </span>
                      ) : null}
                    </span>
                    <span className="flex items-center gap-2">
                      <Badge tone={gorev.status === "agreed" ? "success" : "neutral"}>
                        {CONTRACT_RECONCILIATION_TASK_STATUS_NAMES[gorev.status]}
                      </Badge>
                      {akis.status === "open" &&
                      gorev.status === "pending" &&
                      gorev.assigneeUserId === user?.id ? (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() =>
                            setKararHedef({ reconciliationId: akis.id, taskId: gorev.id })
                          }
                        >
                          Karar ver
                        </Button>
                      ) : null}
                      {/*
                        ⚠ YALNIZ KENDİ MUTABIK BEYANINA sunuluyor — sunucudaki kapının aynısı.
                        Ekran kapı değil ama yanlış kişiye düğme göstermek, tıklayınca 403
                        alan bir yol açardı.

                        ⚠ Düğme akış durumuna BAKMIYOR: mutabakat tamamlanmış (`agreed`)
                        olabilir ve raporun istediği tam olarak o — "tamamlanmış mutabakatın
                        geri çekilmesi". İmza başlamışsa sunucu reddediyor ve sebebi
                        kullanıcıya hata olarak dönüyor; burada tahmin etmiyoruz.
                      */}
                      {beyanGeriCekilebilir({
                        gorevDurumu: gorev.status,
                        atananUserId: gorev.assigneeUserId,
                        aktifUserId: user?.id,
                      }) ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setGeriCekHedefi({ reconciliationId: akis.id, taskId: gorev.id })
                          }
                        >
                          Beyanı geri çek
                        </Button>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <MutabakatBaslatDialog
        open={baslatAcik}
        contractId={contractId}
        onClose={() => setBaslatAcik(false)}
        onDone={async () => {
          setBaslatAcik(false);
          await yenile();
        }}
      />
      <ReconciliationDecisionDialog
        open={kararHedef !== null}
        kararYolu={(hedef) =>
          `/api/contracts/${contractId}/reconciliations/${hedef.reconciliationId}/tasks/${hedef.taskId}/decide`
        }
        hedef={kararHedef}
        onClose={() => setKararHedef(null)}
        onDone={async () => {
          setKararHedef(null);
          await yenile();
        }}
      />

      <WorkflowCancelDialog
        open={iptalHedefi !== null}
        onClose={() => setIptalHedefi(null)}
        title="Mutabakat akışını iptal et"
        description="Açık mutabakat görevleri kapanır ve taraflardan beklenen kararlar düşer. Yeniden mutabakat için akışı baştan başlatmanız gerekir."
        pending={iptal.isPending}
        onConfirm={() => iptalHedefi && iptal.mutate(iptalHedefi)}
      />

      {/*
        ⚠ ONAY PENCERESİ + ZORUNLU GEREKÇE (rapor §9.2).

        Geri çekme, karşı tarafın güvendiği bir beyanı bozuyor; tek tıkla olmamalı. Pencere
        ne olacağını AÇIKÇA söylüyor ve gerekçe boşken düğme kapalı — sunucu da gerekçesiz
        kabul etmiyor, ekran o kuralı önceden gösteriyor.

        ⚠ "Bu işlem eski kararı silmez" cümlesi raporun kendi metninden: kullanıcı geri
        çekmenin geçmişi yok etmediğini bilmeden karar veremez.
      */}
      <Modal
        open={geriCekHedefi !== null}
        onClose={() => {
          setGeriCekHedefi(null);
          setGeriCekGerekce("");
        }}
        title="Mutabakat beyanını geri çek"
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-fg-muted">
            Bu işlem eski mutabakat kararını silmez; geçmiş ve denetim kaydı korunur. Bu versiyon
            artık aktif mutabakatlı sayılmaz.
          </p>
          <FormField label="Gerekçe" required>
            <Textarea
              rows={3}
              value={geriCekGerekce}
              onChange={(e) => setGeriCekGerekce(e.target.value)}
              placeholder="Örn. Madde 13 yeniden müzakereye açıldı"
            />
          </FormField>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setGeriCekHedefi(null);
                setGeriCekGerekce("");
              }}
            >
              Vazgeç
            </Button>
            <Button
              type="button"
              size="sm"
              loading={geriCek.isPending}
              disabled={geriCekGerekce.trim().length === 0}
              onClick={() =>
                geriCekHedefi && geriCek.mutate({ ...geriCekHedefi, reason: geriCekGerekce.trim() })
              }
            >
              Mutabakatı geri çek
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}

/**
 * GERİ ÇEKME DÜĞMESİ KİME SUNULUYOR (rapor §9.2).
 *
 * ⚠ SAF FONKSİYON — dosyanın kendi kuralı: *"ölçülmesi gereken şey bir React ağacı değil
 * bir KARAR."* Düğmenin görünürlüğü bir yetki kararı ve ağaç içinde gömülü kalırsa ancak
 * elle turda fark edilir.
 *
 * ⚠ EKRAN KAPI DEĞİL: sunucu aynı kuralı ayrıca uyguluyor. Buradaki amaç yetkisiz kişiye
 * tıklayınca 403 alacağı bir düğme göstermemek.
 *
 * ⚠ AKIŞ DURUMUNA BAKMIYOR ve bu bilinçli: raporun istediği şey TAMAMLANMIŞ mutabakatın
 * geri çekilmesi, yani akış `agreed` olabilir. İmza başlamışsa sunucu reddediyor; ekran
 * onu tahmin etmeye çalışsaydı, imza durumunu bilmediği anda yanlış cevap verirdi.
 */
export function beyanGeriCekilebilir(girdi: {
  gorevDurumu: string;
  atananUserId: string;
  aktifUserId: string | undefined;
}): boolean {
  if (girdi.gorevDurumu !== "agreed") return false;
  if (!girdi.aktifUserId) return false;
  return girdi.atananUserId === girdi.aktifUserId;
}

/** Mutabakat başlatma — her taraf için bir temsilci; en az iki taraf. */
/**
 * MUTABAKAT BAŞLATMANIN ÖNÜNDEKİ ENGEL — saf ve test edilebilir.
 *
 * ⚠ Diyalogun içinde bir ifade olarak duruyordu; buraya çıkarıldı çünkü ölçülmesi
 * gereken şey bir React ağacı değil bir KARAR: hangi durumda hangi cümle söyleniyor.
 * Onay akışındaki `onayBaslatmaEngeli` ile aynı gerekçe.
 *
 * ⚠ ORTAK BİR SOYUTLAMA YAPILMADI: iki ekranın kuralları farklı (biri "kendin tek
 * onaylayıcı olamazsın", öteki "her tarafa ayrı kişi"). Ortak bir "engel motoru", iki
 * kuralı birbirine benzemeye zorlar ve gün gelip birini yanlış yerde uygulardı.
 */
export function mutabakatBaslatmaEngeli(girdi: {
  versionId: string;
  tarafSayisi: number;
  aktifUyeSayisi: number;
  secilenTarafSayisi: number;
}): string | null {
  /**
   * ⚠ ÖN KOŞUL İLK SIRADA — canlı turda ölçüldü (4 Eylül).
   *
   * Eskiden "sürüm seçilmemiş" kontrolü öndeydi ve kullanıcı modalda sürüm seçtikten
   * sonra davet cümlesini görüyordu: düzeltilebilir bir eksik, düzeltilemez bir engelin
   * önüne geçiyordu. Sıra artık ters — çaresi olmayan engel önce söyleniyor.
   */
  const onKosul = mutabakatOnKosulEngeli(girdi);
  if (onKosul !== null) return onKosul;
  if (girdi.versionId === "") return "Önce mutabakata konu sürümü seçin.";
  if (girdi.secilenTarafSayisi < 2) return "Her iki taraf için de karar verecek kişiyi seçin.";
  return null;
}

/**
 * MUTABAKATIN ÖN KOŞULU — modalın İÇİNDE düzeltilemeyen engeller.
 *
 * ⚠ NEDEN AYRI BİR FONKSİYON: engelin cümlesi doğruydu ama YERİ yanlıştı. Kullanıcı
 * "Mutabakat başlat"a basıyor, modal açılıyor, sürüm seçiyor, tarih giriyor, temsilci
 * menülerini karıştırıyor — ve ancak o emekten SONRA "önce kullanıcı davet edin"
 * cümlesini görüyordu. Oysa o engel modalın içinde düzeltilemez: kullanıcının başka bir
 * ekrana gidip birini davet etmesi gerekiyor.
 *
 * Ayrım şu: modalda düzeltilebilen engel (sürüm/temsilci seçimi) modalda kalır;
 * düzeltilemeyen engel modal AÇILMADAN söylenir. Bu fonksiyon ikinci kümeyi tutuyor ve
 * `mutabakatBaslatmaEngeli` de onu kendi ilk dalı olarak çağırıyor — kural tek yerde
 * yaşıyor, iki yerden okunuyor.
 */
export function mutabakatOnKosulEngeli(girdi: {
  tarafSayisi: number;
  aktifUyeSayisi: number;
}): string | null {
  /**
   * ⚠ Taraf sayısı ÖNCE: karşı taraf hiç eklenmemişken "kullanıcı davet edin" demek,
   * yanlış ekrana yollamak olurdu. Yeni yüklenen bir sözleşmede tipik hâl bu.
   */
  if (girdi.tarafSayisi < 2) {
    return (
      "Mutabakat için sözleşmede en az iki taraf olmalı; önce Taraflar bölümünden karşı " +
      "tarafı ekleyin."
    );
  }
  if (girdi.aktifUyeSayisi < girdi.tarafSayisi) {
    return (
      "Her taraf için ayrı bir karar verici gerekiyor; bir kişi yalnız bir tarafı " +
      "temsil edebilir. Önce çalışma alanınıza kullanıcı davet edin."
    );
  }
  return null;
}

function MutabakatBaslatDialog({
  open,
  contractId,
  onClose,
  onDone,
}: {
  open: boolean;
  contractId: string;
  onClose: () => void;
  onDone: () => Promise<void> | void;
}) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();

  const [versionId, setVersionId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [temsilciler, setTemsilciler] = useState<Record<string, string>>({});

  const versiyonlar = useQuery({
    enabled: open,
    ...sozlesmeSurumSorgusu(tenantId, contractId),
  });

  const taraflar = useQuery({
    enabled: open,
    queryKey: tenantKey(tenantId, "contract", contractId, "taraflar"),
    queryFn: async () =>
      (
        await api<{ parties: ContractParty[]; missing: string[] }>(
          `/api/contracts/${contractId}/parties`,
          { tenantId },
        )
      ).data,
  });

  const uyeler = useQuery({
    enabled: open,
    ...sozlesmeAdaylariSorgusu(tenantId, contractId),
  });

  /**
   * TARAF BAŞINA UYGUN AKTÖRLER — SUNUCU OTORİTESİ (Mega E2E BUG-011).
   *
   * ⚠ Eskiden bu seçici çalışma alanının BÜTÜN aktif üyelerini HER tarafa basıyordu; bir
   * hukuk bürosu kendi çalışanını karşı taraf adına karar verici yapabiliyordu. Aday listesi
   * artık sunucudan, kapının kendisiyle AYNI kuraldan geliyor (`lib/eligibleActors.ts`).
   * İstemcide ikinci bir süzgeç yazmıyoruz: iki kural bir gün ayrışırdı.
   */
  const tarafKimlikleri = (taraflar.data?.parties ?? []).map((t) => t.id);
  const uygunAktorler = useQueries({
    queries: tarafKimlikleri.map((partyId) => ({
      enabled: open,
      queryKey: tenantKey(tenantId, "contract", contractId, "uygun-aktor", partyId, "reconcile"),
      queryFn: async () =>
        (
          await api<{ actors: { userId: string; tenantId: string; name: string }[] }>(
            `/api/contracts/${contractId}/parties/${partyId}/eligible-actors?capability=contract.reconcile`,
            { tenantId },
          )
        ).data.actors,
    })),
  });
  const tarafAdaylari = new Map<string, { userId: string; tenantId: string; name: string }[]>(
    tarafKimlikleri.map((partyId, i) => [partyId, uygunAktorler[i]?.data ?? []]),
  );

  useEffect(() => {
    if (!open) return;
    setTemsilciler({});
    setDueDate("");
    setVersionId(versiyonlar.data?.[0]?.id ?? "");
  }, [open, versiyonlar.data]);

  const aktifUyeler = uyeler.data?.candidates ?? [];
  const secilenTaraflar = Object.entries(temsilciler).filter(([, userId]) => userId !== "");
  const tarafSayisi = (taraflar.data?.parties ?? []).length;

  /**
   * ⚠ ENGELİN SEBEBİ SÖYLENİYOR — canlı turda ölçüldü (4 Eylül).
   *
   * Düğme doğru biçimde pasifti ama HİÇBİR ŞEY söylemiyordu: kullanıcı tıklıyor, ağ
   * isteği bile gitmiyor, hata da yok. Onay akışındaki kusurun birebir kardeşi.
   *
   * ⚠ Bu, tek kişilik yeni bir çalışma alanında KAÇINILMAZ bir durum: karşı taraf henüz
   * sisteme davet edilmediği için onun adına seçilebilecek kimse yok, ve bir kişi yalnız
   * BİR tarafı temsil edebildiği için (aşağıdaki süzgeç) ikinci liste boş kalıyor.
   * Kullanıcı boş bir açılır menüye bakıp neden ilerleyemediğini anlamıyordu.
   */
  const engel = mutabakatBaslatmaEngeli({
    versionId,
    tarafSayisi,
    aktifUyeSayisi: aktifUyeler.length,
    secilenTarafSayisi: secilenTaraflar.length,
  });

  const baslat = useMutation({
    mutationFn: async () =>
      api(`/api/contracts/${contractId}/reconciliations`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({
          versionId,
          parties: secilenTaraflar.flatMap(([partyId, assigneeUserId]) => {
            const aday = (tarafAdaylari.get(partyId) ?? []).find(
              (a) => a.userId === assigneeUserId,
            );
            /**
             * ⚠ Adayı çözülemeyen satır gövdeye HİÇ GİRMİYOR. Seçenekler zaten bu listeden
             * çiziliyor, yani pratikte olmuyor; ama `undefined` bir kiracı göndermek
             * sunucuya "alan yok" dedirtirdi ve kullanıcı sebebini anlamazdı.
             */
            if (!aday) return [];
            return [
              {
                partyId,
                assigneeUserId,
                /**
                 * ⚠ TEMSİLCİNİN KENDİ KİRACISI — çağıranınki DEĞİL (Mega E2E yerel koşum).
                 *
                 * BUG-011 aday listesini sunucuya taşıyınca karşı tarafın üyeleri ilk kez
                 * seçilebilir oldu; ama burada sabit `tenantId` gidiyordu. Sunucu
                 * (`temsilciyiDogrula`) kişiyi o kiracının üyesi sanıp üyeliği orada arıyor,
                 * bulamıyor ve 403 dönüyordu — mutabakat hiç başlatılamıyordu. Sabit, liste
                 * yalnız kendi üyelerini gösterirken doğruydu; kapsam genişleyince yanlış oldu.
                 *
                 * ⚠ Kiracıyı ekran TÜRETMİYOR: `eligible-actors` her aday için `tenantId`
                 * döndürüyor ve kapı da aynı değeri doğruluyor. Burada `tenantId`'ye düşmek,
                 * iki kuralın ayrışmasına açık kapı bırakırdı — bu yüzden aday bulunamazsa
                 * satır gövdeye HİÇ girmiyor.
                 */
                assigneeTenantId: aday.tenantId,
              },
            ];
          }),
          /** ⚠ Son tarih ZORUNLU (ADR-0065); düğme boş tarihte kapalı olduğu için HER ZAMAN gider. */
          dueDate: new Date(`${dueDate}T23:59:59`).toISOString(),
        }),
      }),
    onSuccess: async () => {
      toast.success("Mutabakat başlatıldı");
      await onDone();
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Mutabakat başlatılamadı"),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Mutabakat başlat"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            onClick={() => baslat.mutate()}
            loading={baslat.isPending}
            disabled={engel !== null || dueDate === ""}
          >
            Başlat
          </Button>
        </>
      }
    >
      {versiyonlar.isPending || taraflar.isPending || uyeler.isPending ? (
        <div role="status" aria-label="Seçenekler yükleniyor">
          <Skeleton lines={3} />
        </div>
      ) : (taraflar.data?.parties ?? []).length < 2 ? (
        <p className="text-sm text-fg-muted">
          Mutabakat en az iki taraf gerektirir; önce sözleşmeye tarafları ekleyin.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <FormField label="Mutabakata konu sürüm">
            <Select
              id="mutabakat-versiyon"
              value={versionId}
              onChange={(olay) => setVersionId(olay.target.value)}
            >
              {(versiyonlar.data ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  Sürüm {v.versionNumber} · {new Date(v.createdAt).toLocaleDateString("tr-TR")}
                </option>
              ))}
            </Select>
          </FormField>

          <UyeListesiDurumu hata={uyeler.error} bosMu={aktifUyeler.length === 0} />

          {/* ⚠ Engel VARSA yazılıyor: her zaman görünen bir uyarı, gerçekten engellendiği anda fark edilmez. */}
          {engel ? (
            <p className="text-xs text-warning" data-testid="mutabakat-engel">
              {engel}
            </p>
          ) : null}

          {(taraflar.data?.parties ?? []).map((taraf) => (
            <FormField key={taraf.id} label={`${taraf.displayName} adına karar verecek kişi`}>
              <Select
                id={`mutabakat-temsilci-${taraf.id}`}
                value={temsilciler[taraf.id] ?? ""}
                onChange={(olay) =>
                  setTemsilciler((onceki) => ({ ...onceki, [taraf.id]: olay.target.value }))
                }
              >
                <option value="">Seçilmedi</option>
                {(tarafAdaylari.get(taraf.id) ?? [])
                  /** ⚠ Bir kişi yalnız BİR tarafı temsil edebilir: seçilen kişi diğer listelerden düşüyor. */
                  .filter(
                    (u) =>
                      !Object.entries(temsilciler).some(
                        ([pid, uid]) => pid !== taraf.id && uid === u.userId,
                      ),
                  )
                  .map((u) => (
                    <option key={u.userId} value={u.userId}>
                      {u.name}
                    </option>
                  ))}
              </Select>
            </FormField>
          ))}

          <FormField
            label="Son tarih"
            required
            hint="Süreç başlatılırken son tarih seçilmelidir (ADR-0065)."
          >
            <Input
              id="mutabakat-son-tarih"
              type="date"
              required
              value={dueDate}
              onChange={(olay) => setDueDate(olay.target.value)}
            />
          </FormField>
        </div>
      )}
    </Modal>
  );
}

/** Taraf kararı — mutabık, mutabık değil, değişiklik istendi. */
